// src/screens/ExploreTrainers.tsx
import React, { useMemo, useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, Sparkles } from "lucide-react";
import Title from "../components/Title";
import DaySelector from "../components/DaySelector";
import OverflowHeader from "../components/OverflowHeader";
import TrainerScheduleCard from "../components/TrainerScheduleCard";
import CreditsBalance from "../components/CreditBalance";
import Navbar from "../components/Navbar";
import trainerImg from "../assets/trainer.png";
import { getAllTrainers, getTrainerProfileWithAvailability, getUserProfile, checkSlotAvailability } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import { formatTimeSlot, utcTimeOfDayToLocal } from "../utils/timeUtils";
import { parseSlotToLocalTime } from "../utils/utcTimeUtils";
import { UPLOADS_BASE_URL } from "../config/api";

/* --------------------------------- types ---------------------------------- */
type Trainer = {
  _id: string;
  name: string;
  rate: number;
  age: number;
  sex: "M" | "F";
  schedule: { day: string; time: string }[];
  imageUrl?: string;
  availabilitySlots?: Record<string, { slot: string; available: boolean }[]>;
  fullTrainerData?: any;
  isGym?: boolean;
};

/* ------------------------------ tiny atoms -------------------------------- */

const SegmentedTwo: React.FC<{
  left: { icon: React.ReactNode; label: string };
  right: { icon: React.ReactNode; label: string };
  value: "left" | "right";
  onChange: (v: "left" | "right") => void;
}> = ({ left, right, value, onChange }) => {
  const base = (selected: boolean) =>
    `flex flex-col justify-between items-start p-4 pb-3 gap-4 w-[152px] h-[88px] border-2 bg-white ${selected ? "border-[#D62422]" : "border-[#B1B1B1]"
    }`;

  return (
    <div className="flex gap-4">
      <button
        className={base(value === "left")}
        onClick={() => onChange("left")}
      >
        <div className="w-6 h-6 text-[#D62422]">
          {left.icon}
        </div>
        <span className="font-manrope font-extrabold text-[14px] leading-5 tracking-[0.02em] uppercase whitespace-nowrap">
          {left.label}
        </span>
      </button>
      <button
        className={base(value === "right")}
        onClick={() => onChange("right")}
      >
        <div className="w-6 h-6 text-[#D62422]">
          {right.icon}
        </div>
        <span className="font-manrope font-extrabold text-[14px] leading-5 tracking-[0.02em] uppercase whitespace-nowrap">
          {right.label}
        </span>
      </button>
    </div>
  );
};

const buildDaysData = (count = 7) => {
  const days = [];
  const dayNames = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
  const monthNames = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
  const today = new Date();
  for (let i = 0; i < count; i++) {
    const date = new Date(today);
    date.setDate(today.getDate() + i);
    const month = monthNames[date.getMonth()];
    const day = String(date.getDate()).padStart(2, "0");
    const fullDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${day}`;
    days.push({
      date: `${month} ${day}`,
      day: dayNames[date.getDay()],
      fullDate
    });
  }
  return days;
};

/* --------------------------------- screen --------------------------------- */
export default function ExploreTrainers() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [seg, setSeg] = useState<"left" | "right">("right");
  const [selectedDay, setSelectedDay] = useState(0);
  const [allTrainers, setAllTrainers] = useState<Trainer[]>([]);
  const [loading, setLoading] = useState(true);
  const [userCredits, setUserCredits] = useState(0);

  const [topTrainers, setTopTrainers] = useState<Trainer[]>([]);

  // ── drag-to-scroll with inertia for Top Trainers horizontal list ─────────
  const topTrainersScrollRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const dragScrollLeft = useRef(0);
  const hasDragged = useRef(false);
  const velocity = useRef(0);
  const lastX = useRef(0);
  const animFrame = useRef<number | null>(null);

  const onMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = topTrainersScrollRef.current;
    if (!el) return;
    if (animFrame.current !== null) cancelAnimationFrame(animFrame.current);
    isDragging.current = true;
    hasDragged.current = false;
    velocity.current = 0;
    lastX.current = e.pageX;
    dragStartX.current = e.pageX - el.getBoundingClientRect().left;
    dragScrollLeft.current = el.scrollLeft;
    el.style.cursor = "grabbing";
    el.style.userSelect = "none";
  };

  const onMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging.current) return;
    const el = topTrainersScrollRef.current;
    if (!el) return;
    const x = e.pageX - el.getBoundingClientRect().left;
    const walk = x - dragStartX.current;
    velocity.current = e.pageX - lastX.current;
    lastX.current = e.pageX;
    if (Math.abs(walk) > 4) hasDragged.current = true;
    el.scrollLeft = dragScrollLeft.current - walk;
  };

  const onMouseUp = () => {
    if (!isDragging.current) return;
    isDragging.current = false;
    const el = topTrainersScrollRef.current;
    if (el) { el.style.cursor = "grab"; el.style.userSelect = ""; }
    // inertia
    let v = velocity.current * 0.8;
    const glide = () => {
      const el = topTrainersScrollRef.current;
      if (!el || Math.abs(v) < 0.5) return;
      el.scrollLeft -= v;
      v *= 0.92;
      animFrame.current = requestAnimationFrame(glide);
    };
    animFrame.current = requestAnimationFrame(glide);
  };
  // ─────────────────────────────────────────────────────────────────────────

  const daysData = useMemo(() => buildDaysData(7), []);
  const selectedDayData = daysData[selectedDay] || daysData[0];

  const getAvailableTimesForDate = (trainer: Trainer, fullDate: string) => {
    const daySlots = trainer.availabilitySlots?.[fullDate] || [];
    const availableSlots = daySlots.filter(slot => slot.available);
    const times = availableSlots.map(slot => parseSlotToLocalTime(slot.slot));
    return times.filter(Boolean);
  };

  const availableTrainersForDay = useMemo(() => {
    if (!selectedDayData) return [];
    return allTrainers.filter((trainer) => {
      const times = getAvailableTimesForDate(trainer, selectedDayData.fullDate);
      return times.length > 0;
    });
  }, [allTrainers, selectedDayData]);
  // Fetch real trainer data
  useEffect(() => {
    const fetchTrainers = async () => {
      if (!user?.id) return;

      try {
        setLoading(true);
        console.log('ExploreTrainers - Starting to fetch data for user:', user.id);

        const [trainersResponse, profileResponse] = await Promise.all([
          getAllTrainers(),
          getUserProfile(user.id)
        ]);

        console.log('ExploreTrainers - Trainers response:', trainersResponse);
        console.log('ExploreTrainers - Trainers array:', trainersResponse.trainers);
        console.log('ExploreTrainers - Number of trainers:', trainersResponse.trainers?.length);
        console.log('ExploreTrainers - Profile response:', profileResponse);

        // Set user credits
        if (profileResponse?.user) {
          setUserCredits(profileResponse.user.credits || 0);
        }

        // Format trainers with availability data
        const formattedTrainers = (trainersResponse.trainers || []).map((trainer: any) => {
          try {
            // Get the next 3 days
            const today = new Date();
            const next3Days = [];

            for (let i = 0; i < 3; i++) {
              const date = new Date(today);
              date.setDate(today.getDate() + i);
              const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
              
              // Get the YYYY-MM-DD local format string
              const year = date.getFullYear();
              const month = (date.getMonth() + 1).toString().padStart(2, '0');
              const day = date.getDate().toString().padStart(2, '0');
              const dateString = `${year}-${month}-${day}`;

              next3Days.push({
                dayName,
                dateString,
                isToday: i === 0
              });
            }

            // Get availability for each day using pre-calculated slots
            const schedule = next3Days.map((day, index) => {
              const daySlots = trainer.availabilitySlots?.[day.dateString];

              if (daySlots && Array.isArray(daySlots) && daySlots.length > 0) {
                // Find first available slot
                const firstAvailableSlot = daySlots.find(slot => slot.available);

                if (firstAvailableSlot) {
                  const zIndex = firstAvailableSlot.slot.indexOf('Z');
                  let startTimeLocal = "";
                  let endTimeLocal = "";
                  
                  if (zIndex !== -1) {
                      const startISO = firstAvailableSlot.slot.substring(0, zIndex + 1);
                      const endISO = firstAvailableSlot.slot.substring(zIndex + 2);
                      
                      const startDate = new Date(startISO);
                      const endDate = new Date(endISO);
                      
                      startTimeLocal = startDate.toLocaleTimeString('en-US', {
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true
                      });
                      
                      endTimeLocal = endDate.toLocaleTimeString('en-US', {
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true
                      });
                  }

                  return {
                    day: day.isToday ? 'Today' : index === 1 ? 'Tomorrow' : day.dayName,
                    time: startTimeLocal && endTimeLocal ? `${startTimeLocal} - ${endTimeLocal}` : 'Busy'
                  };
                } else {
                  return {
                    day: day.isToday ? 'Today' : index === 1 ? 'Tomorrow' : day.dayName,
                    time: 'Busy'
                  };
                }
              }

              return {
                day: day.isToday ? 'Today' : index === 1 ? 'Tomorrow' : day.dayName,
                time: 'Busy'
              };
            });

            // Use signedProfilePic or profilePic from backend
            let imageUrl = trainerImg as unknown as string;
            const picUrl = trainer.signedProfilePic || trainer.profilePic;
            
            if (picUrl) {
              if (picUrl.startsWith('http://') || picUrl.startsWith('https://')) {
                imageUrl = picUrl;
              } else if (picUrl.startsWith('trainer/')) {
                imageUrl = `${UPLOADS_BASE_URL}/${picUrl}`;
              } else {
                imageUrl = picUrl;
              }
            }

            return {
              _id: trainer._id,
              name: trainer.name || 'Trainer',
              rate: trainer.creditsPerHour || 0,
              age: trainer.age ? parseInt(trainer.age) : 0,
              sex: trainer.sex === 'Male' ? 'M' : trainer.sex === 'Female' ? 'F' : 'M',
              schedule: schedule,
              imageUrl: imageUrl,
              availabilitySlots: trainer.availabilitySlots || {},
              fullTrainerData: trainer // Pass the full trainer object as it has everything we need
            };
          } catch (error) {
            console.error(`Error formatting trainer ${trainer.name}:`, error);
            
            let imageUrl = trainerImg as unknown as string;
            if (trainer.profilePic) {
              if (trainer.profilePic.startsWith('http://') || trainer.profilePic.startsWith('https://')) {
                imageUrl = trainer.profilePic;
              } else if (trainer.profilePic.startsWith('trainer/')) {
                imageUrl = `${UPLOADS_BASE_URL}/${trainer.profilePic}`;
              } else {
                imageUrl = trainer.profilePic;
              }
            }
            return {
              _id: trainer._id,
              name: trainer.name || 'Trainer',
              rate: trainer.creditsPerHour || 0,
              age: trainer.age ? parseInt(trainer.age) : 0,
              sex: trainer.sex === 'Male' ? 'M' : trainer.sex === 'Female' ? 'F' : 'M',
              schedule: [
                { day: 'Today', time: 'Busy' },
                { day: 'Tomorrow', time: 'Busy' },
                { day: 'Day after', time: 'Busy' }
              ],
              imageUrl: imageUrl,
              availabilitySlots: {},
            };
          }
        });

        console.log('ExploreTrainers - Formatted trainers:', formattedTrainers);
        console.log('ExploreTrainers - Number of trainers:', formattedTrainers.length);

        setAllTrainers(formattedTrainers);
        setTopTrainers(formattedTrainers.slice(0, 3)); // Use first 3 trainers for top trainers section

        console.log('ExploreTrainers - Set available trainers, top trainers, and gyms');
      } catch (error) {
        console.error('Error fetching trainers:', error);
        // Fallback to dummy data
        const fallbackTrainers: Trainer[] = [
          { _id: "fallback-1", name: "HELENA PADILLA", rate: 3, age: 27, sex: "F", schedule: [], imageUrl: trainerImg as unknown as string },
          { _id: "fallback-2", name: "TAKERU SEGAWA", rate: 4, age: 27, sex: "M", schedule: [], imageUrl: trainerImg as unknown as string },
          { _id: "fallback-3", name: "SINGDAM KIATMOO9", rate: 2, age: 27, sex: "M", schedule: [], imageUrl: trainerImg as unknown as string },
        ];
        setAllTrainers(fallbackTrainers);
        setTopTrainers(fallbackTrainers.slice(0, 3));
      } finally {
        setLoading(false);
      }
    };

    fetchTrainers();
  }, [user?.id]);

  return (
    <div className="h-[100dvh] bg-[#F7F7F7] overflow-y-auto" style={{ WebkitOverflowScrolling: "touch" }}>
      <div className="w-full">
        <Navbar
          onBack={() => navigate("/student/home")}
          background="transparent"
          right={
            <CreditsBalance
              balance={userCredits}
              variant="white-with-button"
              onAdd={() => navigate(-1)}
            />
          }
        />

        {/* Content */}
        <main className="px-5">
          {/* Navigation Buttons */}
          <section className="pt-4">
            <div className="flex gap-4">
              <button
                onClick={() => navigate("/student/my-trainers")}
                className="flex flex-col justify-between items-start p-4 pb-3 gap-4 w-[152px] h-[88px] border-2 border-[#B1B1B1] bg-white"
              >
                <div className="w-6 h-6 text-[#D62422]">
                  <Heart className="w-6 h-6" />
                </div>
                <span className="font-manrope font-extrabold text-[14px] leading-5 tracking-[0.02em] uppercase whitespace-nowrap">
                  MY TRAINERS
                </span>
              </button>
              <button
                onClick={() => navigate("/student/all-trainers")}
                className="flex flex-col justify-between items-start p-4 pb-3 gap-4 w-[152px] h-[88px] border-2 border-[#B1B1B1] bg-white"
              >
                <div className="w-6 h-6 text-[#D62422]">
                  <Sparkles className="w-6 h-6" />
                </div>
                <span className="font-manrope font-extrabold text-[14px] leading-5 tracking-[0.02em] uppercase whitespace-nowrap">
                  ALL TRAINERS
                </span>
              </button>
            </div>
          </section>

          {/* TOP TRAINERS — horizontal (header is transparent; schedule box has the border) */}
          <section className="pt-6">
            <Title text="// Top Trainers" />
            <div
              ref={topTrainersScrollRef}
              className="mt-3 flex gap-4 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden cursor-grab select-none"
              onMouseDown={onMouseDown}
              onMouseMove={onMouseMove}
              onMouseUp={onMouseUp}
              onMouseLeave={onMouseUp}
            >
              {topTrainers.map((t, i) => (
                <div key={i} className="w-[300px] flex-shrink-0">
                  <TrainerScheduleCard
                    name={t.name}
                    rate={t.rate}
                    meta={`${t.age}${t.sex}`}
                    imageSrc={String(t.imageUrl || trainerImg)}
                    gradient="red"
                    imgHeight={140}
                    bandHeight={100}
                    schedule={t.schedule}
                    onClick={() => {
                      if (hasDragged.current) return;
                      navigate(`/student/trainer-details`, {
                        state: {
                          trainer: {
                            ...t,
                            fullTrainerData: t.fullTrainerData
                          },
                          from: '/student/explore-trainers'
                        }
                      });
                    }}
                  />
                </div>
              ))}
            </div>
          </section>

          {/* AVAILABLE TRAINERS — vertical list */}
          <section className="pt-6">
            <Title text="// Available Trainers" />

            <div className="mt-3">
              <DaySelector days={daysData} selectedDay={selectedDay} setSelectedDay={setSelectedDay} />
            </div>

            <div className="mt-4 space-y-4">
              {availableTrainersForDay.map((t, i) => {
                const times = selectedDayData
                  ? getAvailableTimesForDate(t, selectedDayData.fullDate)
                  : [];
                const schedule = times.length > 0
                  ? times.slice(0, 3).map((time) => ({ day: selectedDayData.day, time }))
                  : [{ day: selectedDayData?.day || "Day", time: "Busy" }];

                return (
                  <div
                    key={i}
                    className="cursor-pointer"
                    onClick={() => navigate(`/student/trainer-details`, {
                      state: {
                        trainer: {
                          ...t,
                          fullTrainerData: t.fullTrainerData
                        },
                        from: '/student/explore-trainers'
                      }
                    })}
                  >
                    <OverflowHeader
                      name={t.name}
                      rate={t.rate}
                      meta={`${t.age}${t.sex}`}
                      imageSrc={String(t.imageUrl || trainerImg)}
                      gradient="dark"
                      imgHeight={140}
                      bandHeight={100}
                    />
                    {/* time slots box for available trainers */}
                    <div className="px-5 py-4 bg-white border border-[#DEDEDE] rounded">
                      <div className="flex gap-2 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                        {schedule.map((slot, k) => (
                          <button
                            key={k}
                            className={`px-3 py-2 bg-white border border-[#B1B1B1] whitespace-nowrap flex-shrink-0 ${slot.time === "Busy" ? "cursor-not-allowed" : "hover:bg-gray-50"
                              }`}
                            style={{
                              fontFamily: 'Manrope',
                              fontWeight: 500,
                              fontSize: '12px',
                              lineHeight: '14px',
                              letterSpacing: '0%',
                              textAlign: 'center',
                              color: slot.time === "Busy" ? '#B24025' : '#252525'
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (slot.time !== "Busy") {
                                navigate("/student/session-duration", { state: { trainer: t, time: slot.time } });
                              }
                            }}
                            disabled={slot.time === "Busy"}
                          >
                            {slot.time}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}
